import { useLazyQuery } from '@apollo/client'
import { format, subDays, endOfDay, startOfDay } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@emotion/react'
import { GET_MCAT_SPECIFIC_GAME_ANALYTICS_ATTEMPTS } from 'queries/gamesStats'
import {
  findMCATSpecificGameAnalyticsAttempts,
  findMCATSpecificGameAnalyticsAttempts_findMCATSpecificGameAnalyticsAttempts_result as Result,
  findMCATSpecificGameAnalyticsAttemptsVariables,
} from '__generatedTypes__/findMCATSpecificGameAnalyticsAttempts'
import { stripTimezone } from 'utils/stripTimezone'

const PER_PAGE = 10
const PER_PAGE_MOBILE = 7

type Analytics = Record<
  string,
  {
    date: string
    result?: Result
  }
>

const getDate = (date: Date) => format(date, 'yyyy-MM-dd')

export const useChartData = (subjectId: string) => {
  const theme = useTheme()
  const isLessThanTablet = useMediaQuery(theme.breakpoints.down(768))

  const perPage = isLessThanTablet ? PER_PAGE_MOBILE : PER_PAGE

  const [page, setPage] = useState(1)

  const [dailyTimeSpentData, setDailyTimeSpentData] = useState<Analytics>({})
  const [isInitiallyLoaded, setIsInitiallyLoaded] = useState(false)

  const [fetchGeneralAnalytics, { loading }] = useLazyQuery<
    findMCATSpecificGameAnalyticsAttempts,
    findMCATSpecificGameAnalyticsAttemptsVariables
  >(GET_MCAT_SPECIFIC_GAME_ANALYTICS_ATTEMPTS)

  useEffect(() => {
    const abortController = new AbortController()

    ;(async () => {
      try {
        const { data } = await fetchGeneralAnalytics({
          variables: {
            subjectId,
            startDate: startOfDay(subDays(new Date(), page * perPage - 1)),
            endDate: endOfDay(subDays(new Date(), (page - 1) * perPage)),
          },
          fetchPolicy: 'cache-and-network',
          context: {
            fetchOptions: {
              signal: abortController.signal,
            },
          },
        })

        const getData = () =>
          data?.findMCATSpecificGameAnalyticsAttempts?.reduce((acc, item) => {
            const date = getDate(new Date(stripTimezone(item.date)))

            return {
              ...acc,
              [date]: {
                date,
                result: item.result,
              },
            }
          }, {})

        setDailyTimeSpentData((dailyData) => ({
          ...dailyData,
          ...getData(),
        }))
      } catch (e) {
        console.error('Error fetching general analytics', e)
      } finally {
        setIsInitiallyLoaded(true)
      }
    })()
  }, [subjectId, page, perPage])

  const handlePrevClick = () =>
    setPage((page) => {
      const newPage = page + 1
      return newPage
    })

  const handleNextClick = () =>
    setPage((page) => {
      const newPage = page - 1
      return newPage
    })

  const chartData = useMemo(() => {
    const currentDate = new Date()

    const subDaysCount = page * perPage - 1

    return new Array(perPage).fill(0).map((_, index) => {
      const itemDate = subDays(currentDate, subDaysCount - index)
      const dateKey = getDate(itemDate)
      const point = dailyTimeSpentData?.[dateKey]

      const xAxisValue = format(itemDate, 'MMM dd')

      if (point) {
        return {
          xAxisValue,
          yAxisValue: point.result?.attempts || 0,
          result: point.result,
          dateRaw: itemDate,
        }
      }

      return {
        xAxisValue,
        yAxisValue: 0,
        result: undefined,
        dateRaw: itemDate,
      }
    })
  }, [page, perPage, dailyTimeSpentData])

  return {
    page,
    isInitiallyLoaded,
    isFetchingMore: loading,
    chartData,
    onPrevClick: handlePrevClick,
    onNextClick: page !== 1 ? handleNextClick : undefined,
  }
}
